// src/components/groups/InvitationOutcome.jsx
// The end of a personal invitation (#/invite/) or a shared join link (#/join/), Roadmap #54: in,
// already in, or a plain explanation of why the invitation or link can't be used (Build 2 §1:
// "an expired or revoked token … shows a plain explanation, not an error"). One code in, one
// translated title and line out.

import React from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import StandardButton from '../core/StandardButton';
import InvitationPageShell, { InvitationActions, InvitationText } from './InvitationPageShell';

export const goHome = () => {
  window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'feed' } }));
};

export const goToGroup = (groupId) => {
  window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'group', groupId } }));
};

// Reasons whose wording differs between a personal invitation and a shared link.
const KIND_SPECIFIC = new Set(['expired', 'revoked']);
const SHARED_REASONS = new Set([
  'already_used', 'not_found', 'group_unavailable', 'blocked', 'rate_limited', 'not_authenticated'
]);

/**
 * @param {'invite'|'link'} kind
 * @param {string} code — joined · already_member · or a reason the invitation/link can't be used
 */
const InvitationOutcome = ({ kind, code, groupId = null, groupName = null }) => {
  const { t } = useTranslation('groups');

  if (code === 'joined' || code === 'already_member') {
    const title = code === 'joined'
      ? (groupName ? t('join_welcome_title', { group: groupName }) : t('join_welcome_title_no_name'))
      : (groupName ? t('join_already_member_title', { group: groupName }) : t('join_already_member_title_no_name'));
    return (
      <InvitationPageShell status="success" title={title}>
        {code === 'joined' && <InvitationText>{t('join_welcome_body')}</InvitationText>}
        <InvitationActions>
          {groupId
            ? <StandardButton variant="action" iconName="Users" label={t('join_go_to_group')} onClick={() => goToGroup(groupId)} />
            : <StandardButton variant="action" iconName="Home" label={t('join_go_home')} onClick={goHome} />}
        </InvitationActions>
      </InvitationPageShell>
    );
  }

  const reasonKey = KIND_SPECIFIC.has(code)
    ? `${kind}_reason_${code}`
    : SHARED_REASONS.has(code) ? `join_reason_${code}` : 'join_reason_failed';
  return (
    <InvitationPageShell status="error" title={t(kind === 'link' ? 'link_unusable_title' : 'invite_unusable_title')}>
      {/* Keys chosen above by variable: invite_reason_expired, invite_reason_revoked,
          link_reason_expired, link_reason_revoked, join_reason_already_used, join_reason_not_found,
          join_reason_group_unavailable, join_reason_blocked, join_reason_rate_limited,
          join_reason_not_authenticated, join_reason_failed (TRANSLATION.md § Common Pitfalls #1). */}
      <InvitationText>{t(reasonKey)}</InvitationText>
      <InvitationActions>
        <StandardButton variant="action" iconName="Home" label={t('join_go_home')} onClick={goHome} />
      </InvitationActions>
    </InvitationPageShell>
  );
};

export default InvitationOutcome;
